import React, { createContext, useContext, useState, useCallback } from 'react';
import ConfirmDialog from '../components/ui/ConfirmDialog';

interface ConfirmOptions {
    title?: string;
    message: string;
    onConfirm: () => void;
    onCancel?: () => void;
}

interface ConfirmContextType {
    showConfirm: (options: ConfirmOptions) => void;
}

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [config, setConfig] = useState<ConfirmOptions>({
        title: 'Confirmación',
        message: '',
        onConfirm: () => { },
    });

    const showConfirm = useCallback((options: ConfirmOptions) => {
        setConfig({
            title: options.title || 'Confirmación',
            message: options.message,
            onConfirm: () => {
                options.onConfirm();
                setIsOpen(false);
            },
            onCancel: () => {
                if (options.onCancel) options.onCancel();
                setIsOpen(false);
            }
        });
        setIsOpen(true);
    }, []);

    const handleCancel = () => {
        if (config.onCancel) config.onCancel();
        setIsOpen(false);
    };

    return (
        <ConfirmContext.Provider value={{ showConfirm }}>
            {children}
            <ConfirmDialog
                isOpen={isOpen}
                title={config.title || 'Confirmación'}
                message={config.message}
                onConfirm={config.onConfirm}
                onCancel={handleCancel}
            />
        </ConfirmContext.Provider>
    );
};

export const useConfirm = () => {
    const context = useContext(ConfirmContext);
    if (!context) {
        throw new Error('useConfirm must be used within a ConfirmProvider');
    }
    return context;
};
